import React, { useCallback, useState } from 'react';
import { Button, Checkbox, Flex, message, Modal } from 'antd';
import { useToken } from '@/common/helper';
import useUnauthorize from '@/hooks/useUnauthorize';
import { useSpin } from '@/components/SpinContext';
import { ExportReport } from '@/apis/gxpReport';
interface ExportReportModalProps {
  reportID?: string;
  open?: boolean;
  referenceID?: string;
  noticeText?: string;
  onCancel?: () => void;
  onExport?: (options: {
    temperaturePdf: boolean;
    rawDataCsv: boolean;
  }) => void;
}

const ExportReportModal: React.FC<ExportReportModalProps> = ({
  reportID,
  open,
  referenceID,
  onCancel,
}) => {
  const [temperaturePdf, setTemperaturePdf] = useState(true);
  const [rawDataCsv, setRawDataCsv] = useState(false);

  const { token } = useToken();
  const { unauthorize } = useUnauthorize();
  const { showSpin, hideSpin } = useSpin();

  // Determine the export format based on the user's selections:
  // - PDF only  "pdf"
  // - CSV only  "csv"
  // - Both PDF and CSV  "zip"
  // - Neither selected  ""

  const handlePublishReport = useCallback(
    (is_PDF?: boolean, is_CSV?: boolean) => {
      showSpin();
      let exportFormat = '';

      if (is_PDF && is_CSV) {
        exportFormat = 'zip';
      } else if (is_PDF) {
        exportFormat = 'pdf';
      } else if (is_CSV) {
        exportFormat = 'csv';
      }

      const queryParam = `${reportID}/export/?export_format=${exportFormat}`;
      ExportReport(queryParam, token)
        .then((res) => {
          const blob: Blob = res?.data;
          if (!blob || blob.size === 0) return;

          const disposition: string =
            res?.headers?.['content-disposition'] ?? '';
          const filenameMatch = disposition.match(
            /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
          );
          const filename = filenameMatch
            ? filenameMatch[1].replace(/['"]/g, '').trim()
            : `report.${exportFormat}`;

          const url = URL.createObjectURL(blob);
          const anchor = document.createElement('a');
          anchor.href = url;
          anchor.download = filename;
          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
          URL.revokeObjectURL(url);

          message.success('Export report successfully.');
          onCancel();
        })
        .catch((error) => {
          unauthorize(error);
        })
        .finally(() => {
          hideSpin();
        });
    },
    [reportID, token, showSpin, hideSpin, unauthorize, onCancel]
  );

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      centered
      width={460}
      className='gxp-modal'
      destroyOnClose
    >
      <Flex vertical gap={28}>
        <Flex vertical gap={4}>
          <div className='gxp-modal__title'>Export report</div>
          <div className='gxp-modal__subtitle'>Shipment {referenceID}</div>
        </Flex>
        <div className='gxp-modal__description'>
          {
            'This report is pending review. Exported copies are watermarked and are not for external distribution.'
          }
        </div>

        <Flex vertical gap={8}>
          <label className='gxp-modal__checkbox-row'>
            <Checkbox
              checked={temperaturePdf}
              onChange={(e) => setTemperaturePdf(e.target.checked)}
            />
            <span className='gxp-modal__checkbox-copy'>
              <span className='gxp-modal__checkbox-label'>
                Temperature report (PDF)
              </span>
              <span className='gxp-modal__checkbox-description'>
                This page. 1 PDF per sensor; multiple sensors download as zip
              </span>
            </span>
          </label>

          <label className='gxp-modal__checkbox-row'>
            <Checkbox
              checked={rawDataCsv}
              onChange={(e) => setRawDataCsv(e.target.checked)}
            />
            <span className='gxp-modal__checkbox-copy'>
              <span className='gxp-modal__checkbox-label'>Raw data (CSV)</span>
              <span className='gxp-modal__checkbox-description'>
                Value and timestamp per sensor
              </span>
            </span>
          </label>
        </Flex>
        <Flex vertical gap={8}>
          <Button
            type='primary'
            block
            className='gxp-modal__submit-btn'
            disabled={!temperaturePdf && !rawDataCsv}
            onClick={() => handlePublishReport(temperaturePdf, rawDataCsv)}
          >
            Export
          </Button>
          <button
            type='button'
            className='gxp-modal__cancel-link'
            onClick={onCancel}
          >
            Cancel
          </button>
        </Flex>
      </Flex>
    </Modal>
  );
};

export default ExportReportModal;
